import type { BigSource } from 'big.js';
import Big from 'big.js';
import { useCallback, useMemo, useState } from 'react';

import type { Security } from '@talos/kyoko';
import {
  ConnectionStatusEnum,
  ModeEnum,
  Text,
  format,
  isDelta1Multileg,
  logger,
  useConversionRef,
  useDisclosure,
  useObservableRef,
} from '@talos/kyoko';

import { useCurrenciesContext } from '@src/providers';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useTradingLimits } from '@src/providers/TradingLimitsContext';

export const useMaxOrderSizeDialogs = ({
  currency,
  symbol,
  onConfirmWarning,
  security,
}: {
  currency: string | undefined;
  symbol: string | undefined;
  onConfirmWarning: () => void;
  security: Security | undefined;
}) => {
  const limits = useTradingLimits();
  const [warnMessage, setWarnMessage] = useState<React.ReactElement | string | undefined>();
  const [rejectMessage, setRejectMessage] = useState<React.ReactElement | string | undefined>();
  const { homeCurrency } = useDisplaySettings();

  const warnDialog = useDisclosure();
  const warnDialogProps = useMemo(
    () => ({
      usePortal: false,
      cancelLabel: 'No',
      confirmLabel: 'Yes',
      onConfirm: onConfirmWarning,
      onCancel: () => warnDialog.close(),
      close: warnDialog.close,
      isOpen: warnDialog.isOpen,
    }),
    [warnDialog, onConfirmWarning]
  );

  const rejectDialog = useDisclosure();
  const rejectDialogProps = useMemo(
    () => ({
      usePortal: false,
      showConfirm: false,
      cancelLabel: 'OK',
      close: rejectDialog.close,
      isOpen: rejectDialog.isOpen,
    }),
    [rejectDialog]
  );

  const { currenciesBySymbol } = useCurrenciesContext();
  const conversionRef = useConversionRef(currency, symbol, security);
  const openRejectDialog = rejectDialog.open;
  const openWarnDialog = warnDialog.open;
  const maxOrderSizeByKeyRef = useObservableRef(() => limits.maxOrderSizeByKey, [limits.maxOrderSizeByKey]);
  const validate = useCallback(
    ({
      size,
      checkWarn = true,
      checkReject = true,
    }: {
      size: string | undefined;
      checkWarn?: boolean;
      checkReject?: boolean;
    }) => {
      try {
        if (size == null) {
          throw new Error('Max Order Size validation failed. Order size is missing.');
        }
        const maxOrderSize = maxOrderSizeByKeyRef.current;
        if (maxOrderSize == null) {
          return;
        }
        const hasEnabledLimits = maxOrderSize.size && [...maxOrderSize.values()].some(m => m.Mode === ModeEnum.Enabled);
        if (!hasEnabledLimits) {
          return true;
        }

        // https://talostrading.atlassian.net/browse/UI-3582
        if (isDelta1Multileg(security)) {
          return true;
        }

        const conversion = conversionRef?.current;
        if (conversion == null || conversion.Status === ConnectionStatusEnum.Unavailable) {
          logger.warn(`Missing conversion rate for ${currency} while validating max order size`, {
            extra: {
              conversion: JSON.stringify(conversion),
            },
          });
          // If there is no conversion rate, we can't validate the order size and we should allow the order to go through. The backend
          // is also performing this check and will reject the order if the size is too large.
          return true;
        }

        let validationResult;
        try {
          const sizeHomeCurrency = Big(size).times(conversion?.Rate || '1');
          validationResult = limits.validateMaxOrderSize(sizeHomeCurrency, symbol);
        } catch (error) {
          // If validation fails due to missing threshold currency conversions, log and allow order
          logger.error(error as Error, {
            extra: {
              context:
                'Max order size validation threw error. Either missing threshold currency or failed to initialize',
              orderCurrency: currency,
              symbol,
            },
          });
          // Allow the order to proceed - backend will validate
          return true;
        }

        const { warn, reject, limit } = validationResult;

        const formatValue = (value: BigSource, currency: string | undefined) => {
          const currencyDetails = currency ? currenciesBySymbol.get(currency) : undefined;
          return currencyDetails == null
            ? value.toString()
            : format(value, {
                spec: currencyDetails.DefaultIncrement,
                truncate: true,
              });
        };

        const formatToHomeCurrency = (value: BigSource, valueCurrency: string | undefined) => {
          let conversionRate;
          if (currency === valueCurrency) {
            conversionRate = conversion?.Rate;
          } else {
            conversionRate = valueCurrency ? limits.conversionRates.get(valueCurrency)?.Rate : undefined;
          }
          if (conversionRate) {
            return `(${formatValue(Big(conversionRate).times(value), homeCurrency)} ${homeCurrency})`;
          }
          logger.error(
            new Error(
              `Missing conversion rate for ${currency} to ${valueCurrency} while formatting max order size dialog`
            ),
            {
              extra: {
                conversionRate: JSON.stringify(conversionRate),
              },
            }
          );
          throw new Error(`Missing conversion rate for ${valueCurrency}. Please contact Talos support.`);
        };

        if (checkReject && reject && limit) {
          setRejectMessage(
            <p>
              Order size{' '}
              <Text color="colorTextImportant">
                {formatValue(size, currency)} {currency}
              </Text>{' '}
              {currency !== homeCurrency && formatToHomeCurrency(size, currency)} is greater than
              {limit.Description && <Text color="colorTextImportant"> {limit.Description}</Text>} rejection threshold{' '}
              <Text color="colorTextImportant">
                {formatValue(limit.RejectThreshold, limit.ThresholdCurrency)} {limit.ThresholdCurrency}
              </Text>{' '}
              {limit.ThresholdCurrency !== homeCurrency &&
                formatToHomeCurrency(limit.RejectThreshold, limit.ThresholdCurrency)}
              .
            </p>
          );
          openRejectDialog();
          return false;
        }

        if (checkWarn && warn && limit) {
          setWarnMessage(
            <>
              <p>
                Order size{' '}
                <Text color="colorTextImportant">
                  {formatValue(size, currency)} {currency}
                </Text>{' '}
                {currency !== homeCurrency && formatToHomeCurrency(size, currency)} is greater than
                {limit.Description && <Text color="colorTextImportant"> {limit.Description}</Text>} warning threshold{' '}
                <Text color="colorTextImportant">
                  {formatValue(limit.WarnThreshold, limit.ThresholdCurrency)} {limit.ThresholdCurrency}
                </Text>{' '}
                {limit.ThresholdCurrency !== homeCurrency &&
                  formatToHomeCurrency(limit.WarnThreshold, limit.ThresholdCurrency)}
                .
              </p>{' '}
              <p>Are you sure you want to proceed?</p>
            </>
          );
          openWarnDialog();
          return false;
        }
      } catch (e: any) {
        setRejectMessage(e.message as string);
        openRejectDialog();
        return false;
      }

      return true;
    },
    [
      maxOrderSizeByKeyRef,
      conversionRef,
      limits,
      symbol,
      security,
      currency,
      openRejectDialog,
      currenciesBySymbol,
      homeCurrency,
      openWarnDialog,
    ]
  );

  return {
    warnDialog: warnDialogProps,
    warnMessage,
    rejectDialog: rejectDialogProps,
    rejectMessage,
    validate,
  };
};
